import * as React from "react";
import {
    Navigate,
    Route,
    Routes,
    createBrowserRouter,
    Outlet,
} from "react-router";

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { RouterProvider } from "react-router/dom";

import logger from "../log";
import theme, { darkTheme } from "../theme";
import { ToastProvider } from "../util/useToast";

import AskVolleyPage from "./AskVolley/AskVolleyPage";
import Auth from "./Auth";
import BallCabinetUnlock from "./BallCabinetUnlock";
import ClaimLanding from "./Claim/ClaimLanding";
import DialogProvider from "./Dialog/DialogProvider";
import HelpArticle from "./HelpArticle";
import NewHomeScreen from "./Home/NewHomeScreen";
import Login from "./Login";
import Sessions from "./Sessions";
import PairDialog from "./Sessions/Pair/PairDialog";
import StackIndicator from "./StackIndicator";
import Tutorials from "./Tutorials/Tutorials";
import Loading from "./common/Loading";
import ReactLazyError from "./common/ReactLazyError";
import RequireAdmin from "./common/RequireAdmin";
import RequireAuth from "./common/RequireAuth";
import UserPage from "./common/UserPage";
import { SportProvider } from "./common/context/sport";
import { CurrentUserProvider } from "./hooks/currentUser";
import { DebugProvider } from "./hooks/debugView";
import { PairingProvider } from "./hooks/pairingStatus";
import { StatusProvider } from "./hooks/status";
import { IntercomProvider } from "./hooks/useIntercom";
import { JourneyProvider } from "./hooks/useJourney";
import { LocalizationProvider } from "./hooks/usePosition";
import { TrainerFeatureProvider } from "./hooks/useTrainerFeatures";
import { UpdateProvider } from "./hooks/useUpdate";

function handleImportError(e: Error) {
    logger.error(`Error lazy loading component: ${e.message}`, undefined, e);
    return { default: () => <ReactLazyError /> };
}

const Account = React.lazy(() => import("./Account").catch(handleImportError));
const Admin = React.lazy(() => import("./Admin").catch(handleImportError));
const Experiences = React.lazy(() =>
    import("./Experiences").catch(handleImportError),
);
const Journeys = React.lazy(() =>
    import("./Journeys/Journeys").catch(handleImportError),
);
const PlayerPTI = React.lazy(() =>
    import("./PTI/PlayerPTI").catch(handleImportError),
);
const Signup = React.lazy(() => import("./Signup").catch(handleImportError));
const Stats = React.lazy(() =>
    import("./Stats/Stats").catch(handleImportError),
);
const MyGamePage = React.lazy(() =>
    import("./Stats/MyGame/MyGamePage").catch(handleImportError),
);
const Trainer = React.lazy(() => import("./Trainer").catch(handleImportError));
const ProGrading = React.lazy(() =>
    import("./Journeys/ProGrading").catch(handleImportError),
);
const Performance = React.lazy(() =>
    import("./Performance").catch(handleImportError),
);
const DuprDashboard = React.lazy(() =>
    import("./DuprDashboard").catch(handleImportError),
);
const PadelDashboard = React.lazy(() =>
    import("./PadelDashboard").catch(handleImportError),
);

function ContextProviders({
    children,
}: React.PropsWithChildren): React.JSX.Element {
    return (
        <CurrentUserProvider>
            <ToastProvider>
                <PairingProvider>
                    <DebugProvider>
                        <StatusProvider>
                            <SportProvider>
                                <TrainerFeatureProvider>
                                    <IntercomProvider>
                                        <LocalizationProvider>
                                            <JourneyProvider>
                                                <UpdateProvider>
                                                    <DialogProvider>
                                                        {children}
                                                    </DialogProvider>
                                                </UpdateProvider>
                                            </JourneyProvider>
                                        </LocalizationProvider>
                                    </IntercomProvider>
                                </TrainerFeatureProvider>
                            </SportProvider>
                        </StatusProvider>
                    </DebugProvider>
                </PairingProvider>
            </ToastProvider>
        </CurrentUserProvider>
    );
}

function Root(): React.JSX.Element {
    return (
        <Routes>
            {/* Authenticated Routes */}
            <Route
                element={
                    <ContextProviders>
                        <RequireAuth>
                            <Outlet />
                            <PairDialog />
                        </RequireAuth>
                    </ContextProviders>
                }
            >
                {/* Pages with the UserPage wrapper */}
                <Route
                    element={
                        <UserPage>
                            <Outlet />
                        </UserPage>
                    }
                >
                    <Route path="*" element={<NewHomeScreen />} />
                    <Route
                        path="/content/*"
                        element={
                            <React.Suspense fallback={<Loading />}>
                                <Trainer />
                            </React.Suspense>
                        }
                    />
                    <Route
                        path="/player-pti/*"
                        element={
                            <React.Suspense fallback={<Loading />}>
                                <PlayerPTI />
                            </React.Suspense>
                        }
                    />
                    <Route
                        path="/account"
                        element={
                            <React.Suspense fallback={<Loading />}>
                                <Account />
                            </React.Suspense>
                        }
                    />
                    <Route
                        path="/ball-cabinet-unlock"
                        element={<BallCabinetUnlock />}
                    />
                    <Route
                        path="/journeys/*"
                        element={
                            <React.Suspense fallback={<Loading />}>
                                <Journeys publicFacing={false} />
                            </React.Suspense>
                        }
                    />
                    <Route
                        path="/performance/*"
                        element={
                            <React.Suspense fallback={<Loading />}>
                                <Performance />
                            </React.Suspense>
                        }
                    />
                    <Route
                        path="/dupr-dashboard/*"
                        element={
                            <React.Suspense fallback={<Loading />}>
                                <DuprDashboard />
                            </React.Suspense>
                        }
                    />
                    {/* Not under /stats/*, which draws its own bar: My
                        Game needs the app's TopNavBar to get back home. */}
                    <Route
                        path="/stats/my-game"
                        element={
                            <React.Suspense fallback={<Loading />}>
                                <MyGamePage />
                            </React.Suspense>
                        }
                    />
                    {/* Experiences, as players open them. OUTSIDE /admin
                        on purpose: the hardware dialogs (DialogProvider — ball
                        jam, faults) disable themselves on /admin paths. "/exp"
                        rather than "/experiences" because nginx routes the
                        /experience prefix to webby-server. */}
                    <Route
                        path="/exp/*"
                        element={
                            <React.Suspense fallback={<Loading />}>
                                <Experiences />
                            </React.Suspense>
                        }
                    />
                    <Route
                        path="/padel-dashboard/*"
                        element={
                            <React.Suspense fallback={<Loading />}>
                                <PadelDashboard />
                            </React.Suspense>
                        }
                    />
                </Route>

                {/* Routes that implement their own window decorations */}
                <Route
                    path="/admin/*"
                    element={
                        <RequireAdmin>
                            <React.Suspense fallback={<Loading />}>
                                <Admin />
                            </React.Suspense>
                        </RequireAdmin>
                    }
                />
                <Route
                    path="/stats/*"
                    element={
                        <React.Suspense fallback={<Loading />}>
                            <Stats />
                        </React.Suspense>
                    }
                />
                <Route
                    path="/pro-grading/*"
                    element={
                        <ThemeProvider theme={darkTheme}>
                            <CssBaseline />
                            <React.Suspense fallback={<Loading />}>
                                <ProGrading />
                            </React.Suspense>
                        </ThemeProvider>
                    }
                />
                <Route path="/sessions/*" element={<Sessions />} />
                <Route path="/tutorials/*" element={<Tutorials />} />
                <Route path="/help/:slug" element={<HelpArticle />} />
                {/* The chat is a fullscreen overlay and paints its own header,
                    so it sits outside the UserPage wrapper — inside it the nav
                    bar would be rendered behind an opaque fixed layer. */}
                <Route path="/ask" element={<AskVolleyPage />} />
                {/* Retired: the membership explainer moved to Club-in-Club's Welcome
                    screen, opened from the home card's "Activate your Volley
                    membership". Old links land on home, where that card is. */}
                <Route
                    path="/membership/welcome"
                    element={<Navigate to="/" replace />}
                />
            </Route>

            {/* Unauthenticated Routes */}
            <Route
                element={
                    <CurrentUserProvider>
                        <Outlet />
                    </CurrentUserProvider>
                }
            >
                {/* Permit unauthenticated users access to an assessment report card */}
                <Route
                    path="/external-journeys/*"
                    element={
                        <React.Suspense fallback={<Loading />}>
                            <Journeys publicFacing />
                        </React.Suspense>
                    }
                />
                <Route
                    path="/signup/*"
                    element={
                        <React.Suspense fallback={<Loading />}>
                            <Signup />
                        </React.Suspense>
                    }
                />
                {/* Public: the player scanning a claim code may have no account */}
                <Route path="/claim/:code" element={<ClaimLanding />} />
                <Route path="/login" element={<Login />} />
                <Route path="/auth/*" element={<Auth />} />
            </Route>
        </Routes>
    );
}

const router = createBrowserRouter([{ path: "*", element: <Root /> }]);

export default function App(): React.JSX.Element {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <StackIndicator />
            <RouterProvider router={router} />
        </ThemeProvider>
    );
}
